import {
  faBoltSlash,
  faCheck,
  faUpload,
  faXmark
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Chip,
  type ChipOwnProps,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import ListItemText from '@mui/material/ListItemText';
import type { ICellRendererParams } from 'ag-grid-enterprise';
import dayjs from 'dayjs';
import { get, startCase } from 'lodash';

import AnchorWrapper from '@/components/AnchorWrapper';
import MultiPartScoreBadge from '@/components/MultiPartScoreBadge';
import { PriorityChip, type PriorityTypes } from '@/components/PriorityChip';
import VMPGenericChip from '@/components/VMPGenericChip';
import { BOOL_LABELS } from '@/constants/common';
import AssetProviderLogo from '@/features/AssetProviderLogo';
import { scorePartsNoTotal } from '@/pages/Assets';
import {
  ASSET_DATA_PROVIDER_LABELS,
  ASSET_DATA_PROVIDERS,
  ASSET_DATA_PROVIDERS_LOGOS,
  formatHostIsolationExclusionReasons
} from '@/pages/Assets/Assets.constants';
import type { RendererProps } from '@/pages/Assets/Assets.types';
import {
  caseAIStatusFormatter,
  getColorByVerdict
} from '@/pages/Case/Case.utils';
import getDateTime from '@/utils/getDateTime.utils';

export const arrayChipRenderer = (props: ICellRendererParams) => {
  const { colDef, data, value } = props;
  const arrayKey = colDef?.field;

  // Use value if available (from valueGetter), otherwise fall back to data[arrayKey]
  let arrayData: unknown;
  if (value !== undefined) {
    arrayData = value;
  } else if (arrayKey) {
    arrayData = data[arrayKey];
  } else {
    arrayData = null;
  }

  return (
    <>
      {Array.isArray(arrayData) &&
        arrayData.map((label: string) => (
          <Chip key={label} label={label} sx={{ mr: 1 }} />
        ))}
    </>
  );
};

export const assetNameRenderer = (props: ICellRendererParams) => {
  const { data } = props;
  const reasons = Array.isArray(data?.host_isolation_exclusion_reasons)
    ? data.host_isolation_exclusion_reasons
    : [];

  return (
    <Box className="cell-ellipsis">
      <Tooltip title={data?.asset_name}>
        <Box component="span" sx={{ mr: '1rem' }}>
          {data?.asset_name}
        </Box>
      </Tooltip>
      {reasons.length > 0 && (
        <Tooltip
          title={`Asset will be excluded from response actions based on matching criteria for: ${formatHostIsolationExclusionReasons(
            reasons
          )}`}
        >
          <Box
            component="span"
            aria-label="Host isolation exclusion reasons"
            sx={{ color: 'secondary.main', fontSize: '1rem' }}
          >
            <FontAwesomeIcon icon={faBoltSlash} />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export const booleanChipRenderer = ({ value }: ICellRendererParams) => {
  return value === true || value === 'Yes' || value === 'true' ? (
    <Chip label="Yes" color="success" />
  ) : (
    <Chip label="No" />
  );
};

export const categoryRenderer = ({ value }: ICellRendererParams) => {
  return !value ? null : <Chip label={value} color="default" />;
};

export const criticalityRenderer = (props: RendererProps) => {
  const { data } = props;

  if (!data.criticality) return null;

  return (
    data.criticality &&
    data.criticality !== 'None' && (
      <VMPGenericChip
        type="filled"
        value={data.criticality?.toLowerCase()}
        text={`${data.criticality?.toLowerCase() || '0'}`}
      />
    )
  );
};

export const doubleCategoryRenderer = (
  props: ICellRendererParams & {
    category1: string;
    category2: string;
  }
) => {
  const { data, category1, category2 } = props;

  const cat1Value = data[category1];
  const cat2Value = data[category2];

  if (!cat1Value && !cat2Value) {
    return '-- / --';
  }

  // Helper to truncate label
  const truncate = (label: string) =>
    label && label.length > 8 ? `${label.slice(0, 8)}…` : label;

  if (!cat2Value || cat2Value === '') {
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Tooltip title={cat1Value}>
          <Chip label={truncate(cat1Value)} color="default" />
        </Tooltip>
        <span>/ --</span>
      </Stack>
    );
  }
  if (!cat1Value || cat1Value === '') {
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <span>-- /</span>
        <Tooltip title={cat2Value}>
          <Chip label={truncate(cat2Value)} color="default" />
        </Tooltip>
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      <Tooltip title={cat1Value}>
        <Chip label={truncate(cat1Value)} color="default" />
      </Tooltip>
      <span>/</span>
      <Tooltip title={cat2Value}>
        <Chip label={truncate(cat2Value)} color="default" />
      </Tooltip>
    </Stack>
  );
};

export const dataProviderRenderer = (props: ICellRendererParams) => {
  const { colDef, data } = props;
  const arrayKey = colDef?.field;

  return (
    <>
      {arrayKey &&
        data[arrayKey]?.map((label: string) => (
          <AssetProviderLogo key={label} label={label} />
        ))}
    </>
  );
};

export const dateTimeRenderer = ({ colDef, data }: ICellRendererParams) => {
  const dateTimeKey = colDef?.field;

  if (!dateTimeKey || !data?.[dateTimeKey]) return null;

  const inputLocal = dayjs.utc(data[dateTimeKey]).local();
  const nowLocal = dayjs();
  const diffMinutes = inputLocal.diff(nowLocal, 'minute', true);
  const humanized = dayjs.duration(diffMinutes, 'minutes').humanize(true);

  return (
    <Tooltip
      title={data[dateTimeKey] ? getDateTime(String(data[dateTimeKey])) : ''}
    >
      <ListItemText
        primary={humanized}
        secondary={
          data[dateTimeKey] ? getDateTime(String(data[dateTimeKey])) : undefined
        }
        sx={{ margin: 0 }}
      />
    </Tooltip>
  );
};

export const idRenderer = (props: ICellRendererParams) => {
  const { path, colorAnchor, data, idFieldKey, value } =
    props as ICellRendererParams & {
      path?: string;
      colorAnchor?: boolean;
      idFieldKey?: string;
    };

  if (!value) return null;

  const content = (
    <Typography sx={{ fontWeight: 600 }} color="primary">
      {value}
    </Typography>
  );

  // Only wrap with AnchorWrapper if path is provided
  if (path) {
    return (
      <AnchorWrapper
        itemId={idFieldKey ? data[idFieldKey] : value}
        path={path}
        colorAnchor={colorAnchor}
      >
        {content}
      </AnchorWrapper>
    );
  }

  return content;
};

export const priorityRenderer = ({ value }: ICellRendererParams) => {
  if (!value) return null;

  return <PriorityChip priority={value as PriorityTypes} />;
};

export const triplePriorityRenderer = (
  props: ICellRendererParams & {
    priority1: string;
    priority2: string;
    priority3: string;
  }
) => {
  const { data, priority1, priority2, priority3 } = props;

  const priorities = [data[priority1], data[priority2], data[priority3]];

  return (
    <>
      {priorities.map((value, index) => (
        <Stack
          key={value}
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center' }}
        >
          {index > 0 && <span style={{ marginLeft: '0.5rem' }}>/</span>}
          {value && value ? (
            <PriorityChip priority={value as PriorityTypes} />
          ) : (
            <span>--</span>
          )}
        </Stack>
      ))}
    </>
  );
};

export const hasAgentRenderer = (props: RendererProps) => {
  const { data } = props;
  const hasAgent = data.has_agent && data.has_agent.length > 0;

  const chipElement = hasAgent ? (
    <Chip label="Yes" color="success" />
  ) : (
    <Chip label="No" />
  );

  if (!hasAgent) {
    return chipElement;
  }

  return (
    <Tooltip
      title={
        <Stack direction="column" spacing={0.5}>
          {data.has_agent?.map((provider: string) => {
            // Replace 'shield' with 'vmpe' to match the constant definitions
            const normalizedProvider = provider.replace('shield', 'vmpe');
            const providerData = ASSET_DATA_PROVIDERS_LOGOS[normalizedProvider];
            const isManual = normalizedProvider === ASSET_DATA_PROVIDERS.MANUAL;

            let iconElement = null;
            if (providerData?.image) {
              iconElement = (
                <img
                  alt={providerData.alt}
                  src={providerData.image}
                  style={{
                    marginRight: '0.5rem',
                    height: '1em',
                    width: '1em'
                  }}
                />
              );
            } else if (isManual) {
              iconElement = (
                <IconButton
                  disableRipple
                  disableFocusRipple
                  size="small"
                  sx={{
                    p: 0,
                    mr: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'transparent',
                    color: 'text.secondary',
                    borderRadius: 0.5,
                    width: '1em',
                    height: '1em',
                    minWidth: '1em',
                    minHeight: '1em'
                  }}
                >
                  <FontAwesomeIcon size="xs" icon={faUpload} />
                </IconButton>
              );
            }

            return (
              <Stack
                direction="row"
                key={provider}
                sx={{ alignItems: 'center', display: 'flex' }}
              >
                {iconElement}
                {
                  ASSET_DATA_PROVIDER_LABELS[
                    normalizedProvider as keyof typeof ASSET_DATA_PROVIDER_LABELS
                  ]
                }
              </Stack>
            );
          })}
        </Stack>
      }
    >
      {chipElement}
    </Tooltip>
  );
};

export const riskScoreRenderer = (props: RendererProps) => {
  const { data } = props;

  return (
    <MultiPartScoreBadge
      score={data.risk_score || 0}
      parts={scorePartsNoTotal(data.risk_score_details || {})}
    />
  );
};

export const chipRenderer = ({
  colors,
  variant,
  value,
  emptyString = null
}: ICellRendererParams & {
  colors?: Record<string, ChipOwnProps['color']>;
  variant?: ChipOwnProps['variant'];
  emptyString?: string | null; // Value to display when input value is falsy (default: null, which renders nothing)
}) => {
  if (!value) return emptyString;

  return (
    <Chip
      variant={variant || 'outlined'}
      label={value}
      color={colors?.[value] || 'default'}
    />
  );
};

export const doubleStatusRenderer = (
  props: ICellRendererParams & {
    status1: string;
    status2: string;
    status1Colors?: Record<string, ChipOwnProps['color']>;
    status2Colors?: Record<string, ChipOwnProps['color']>;
  }
) => {
  const { data, status1, status2, status1Colors, status2Colors } = props;
  const status1Value = startCase(data[status1]);

  // Special handling for AI status formatting
  const status2Value =
    status2 === 'ai_status'
      ? caseAIStatusFormatter(data[status2])
      : data[status2];
  const color1 = status1Colors?.[status1Value] || 'default';
  const color2 = status2Colors?.[status2Value] || 'default';

  if (!status1Value && !status2Value) {
    return '-- / --';
  }

  if (!status2Value || status2Value === '') {
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Chip variant="outlined" label={status1Value} color={color1} />
        <span>/ --</span>
      </Stack>
    );
  }
  if (!status1Value || status1Value === '') {
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <span>-- /</span>
        <Chip variant="outlined" label={status2Value} color={color2} />
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      <Chip variant="outlined" label={status1Value} color={color1} />
      <span>/</span>
      <Chip variant="outlined" label={status2Value} color={color2} />
    </Stack>
  );
};

export const verdictRenderer = ({ value }: ICellRendererParams) => {
  if (!value) return null;

  const color = getColorByVerdict(value);

  return (
    <Tooltip title={startCase(value)}>
      <Chip label={startCase(value)} variant="twoTone" color={color} />
    </Tooltip>
  );
};

export const textRenderer = ({ colDef, data }: ICellRendererParams) => {
  const fieldKey = colDef?.field;

  if (!fieldKey) return null;

  const value = get(data, fieldKey, '');
  return (
    <Tooltip title={value}>
      <span className="cell-ellipsis">{value}</span>
    </Tooltip>
  );
};

export const checkMarkRenderer = ({ value }: ICellRendererParams) => {
  return value === true ||
    value === BOOL_LABELS.YES ||
    value === BOOL_LABELS.TRUE ||
    value === BOOL_LABELS.ON ? (
    <FontAwesomeIcon
      icon={faCheck}
      style={{ color: 'var(--mui-palette-success-main)' }}
    />
  ) : (
    <FontAwesomeIcon
      icon={faXmark}
      style={{ color: 'var(--mui-palette-error-main)' }}
    />
  );
};

export const loadingCellRenderer = () => {
  return null;
};
